import { useMemo } from 'react';
import { Link, Outlet } from 'react-router';
import { useQuery } from '@apollo/client/react';
import { AGENT_RED_MINUTES } from '@infras/shared-types';
import {
  ATLAS_UNMAPPED,
  PROJECTS_OVERVIEW,
  type AtlasProjectOverview,
  type UnmappedCluster,
} from '../lib/atlas';
import {
  ATLAS_EGRESS_IPS,
  isEgressQueryUnavailable,
  type AtlasEgressIp,
} from '../lib/egress';
import { CapacityBand } from './cluster-access/CapacityBand';

/**
 * `/cluster-access` — one screen for a question that used to need two menus.
 *
 * "Is every cluster's egress IP on the right access list, and is anything holding a
 * slot for nothing?" Before this screen, half the evidence was under Atlas IPs and
 * half under Agents, and the two never referenced each other — even though they are
 * one mechanism: `classifyEntries` classifies an access-list entry from
 * `loadAgentIpLiveness`, which is read off `agents.last_seen_at`. The owner's words
 * for that split were "makes me confuse".
 *
 * THE CLUSTER × ADDRESS TABLE IS GONE (owner decision, Binh, 2026-08-26: "I still
 * think the section Egress addresses is useless … we can detect 0.0.0.0/0 by
 * scanning Access List of clusters. It is actually not useful, clean it up"). It was
 * 458 rows behind four filters, and every row it could show is on the project drawer's
 * access-list table — which is the surface that also carries the removal. The two
 * things the table did that the drawer does not are kept as the tiles below: the
 * fleet-wide slot count, and the clusters whose address reaches no list at all.
 *
 * `0.0.0.0/0` — the one finding nobody would spot by scrolling anyway — is now an
 * explicit red line on the Overview checklist, which is where DevOps acts.
 *
 * WHAT IS STILL DELIBERATELY ABSENT: no selection control on this screen, at any
 * role. Reclaiming a slot lives one drawer deep, behind the same confirm and the same
 * `root` gate as before (AC-6(g)).
 *
 * NOTHING HERE RE-DERIVES A VERDICT. `addressLive` and `reclaimable` come off the
 * wire (AC-9 / D6). Two components disagreeing about what is reclaimable would be
 * two answers to "what is safe to delete".
 */
export function ClusterAccessPage() {
  const overview = useQuery<{ atlasProjectsOverview: AtlasProjectOverview[] }>(PROJECTS_OVERVIEW, {
    fetchPolicy: 'cache-and-network',
  });
  const unmapped = useQuery<{ atlasUnmappedClusters: UnmappedCluster[] }>(ATLAS_UNMAPPED, {
    fetchPolicy: 'cache-and-network',
  });
  // `errorPolicy: 'all'` so a validation failure against an api that predates this
  // query comes back as an error we can READ rather than a thrown render — see D11
  // and `isEgressQueryUnavailable`.
  const egress = useQuery<{ atlasEgressIps: AtlasEgressIp[] }>(ATLAS_EGRESS_IPS, {
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });

  const projects = overview.data?.atlasProjectsOverview ?? [];
  // OQ-10 / AC-27: the slot tiles count the Atlas 200-slot budget. A DigitalOcean
  // firewall has its own cap (100) and its own row on the band, so its keys are
  // excluded from the tiles rather than added to a number they are not part of.
  const doKeys = useMemo(
    () => new Set(projects.filter((p) => p.kind === 'do_db').map((p) => p.projectKey)),
    [projects],
  );
  // Ignored clusters are excluded from the count the band advertises — that is the
  // entire point of being able to ignore one.
  const unmappedActive = (unmapped.data?.atlasUnmappedClusters ?? []).filter((c) => !c.ignored);
  const rows = egress.data?.atlasEgressIps ?? [];
  const unavailable = isEgressQueryUnavailable(egress.error);

  return (
    <div className="page page--wide">
      <div className="detail-head">
        <h2>Cluster access</h2>
        <span className="spacer" />
        <Link className="btn btn-ghost" to="/cluster-access/fleet">
          Agent fleet
        </Link>
        <Link className="btn btn-ghost" to="/cluster-access/commands">
          Command log
        </Link>
        <Link className="btn btn-ghost" to="/docs/agents">
          How it works
        </Link>
      </div>

      <p className="detail-desc">
        Every node runs a DaemonSet agent that reports its egress IP, and every one of those
        addresses has to be on the right access list — a MongoDB Atlas project or a DigitalOcean
        database firewall — or the node cannot reach the database. The agents and the access lists
        are one mechanism, so they are one screen: the targets are below, and each target drawer
        carries its own access list — every address on it, what state it is in, and the removal.
      </p>

      <CapacityBand
        projects={projects}
        loading={overview.loading && !projects.length}
        error={Boolean(overview.error)}
        unmappedActive={unmappedActive.length}
        // Returns the promise so the section's Refresh control can stay busy until the
        // last query lands (AC-11) — a void callback would re-enable the button
        // instantly and claim the table was fresh before it was.
        onRefetch={() =>
          Promise.all([
            overview.refetch().catch(() => undefined),
            unmapped.refetch().catch(() => undefined),
            egress.refetch().catch(() => undefined),
          ])
        }
      />

      {/* The table carried the only error surface this query had, and the tiles read the
          SAME query. Without this, a resolver that throws renders as two em dashes over
          "not available yet" — which is the deploy-skew wording and tells the operator to
          wait for something that will not resolve on its own. The skew case keeps that
          wording (it is true there); a real failure says so. */}
      {Boolean(egress.error) && !unavailable && (
        <p className="error">
          Couldn&apos;t load the egress addresses — the API didn&apos;t respond, which is why the
          two counts below are blank rather than zero. Nothing was changed in Atlas; this screen
          only reads. If it persists, ask in #devops-infra.
        </p>
      )}

      <AnswerTiles rows={rows} known={!unavailable && !!egress.data} doKeys={doKeys} />

      {/* Drawers render here, over this screen. The landing stays mounted, which is
          what lets a detail be read without losing the row that raised the question. */}
      <Outlet />
    </div>
  );
}

/**
 * AC-1 Q2 and Q3, computed only from fields the wire carries.
 *
 * Every tile that can be unknown says so as its own number. A project whose access
 * list has never been read is not evidence that its addresses reach nothing — it is
 * evidence that nobody has looked, and the two must never be added together.
 */
function AnswerTiles({
  rows,
  known,
  doKeys,
}: {
  rows: AtlasEgressIp[];
  known: boolean;
  /** do_db target keys — outside the 200-slot budget the first tile counts. */
  doKeys: Set<string>;
}) {
  const t = useMemo(() => {
    // `heldByGone` was the wrong name for what this counts — it presupposed agents,
    // which is the same presupposition that made the old label false (F-QC1 →
    // reviewer 22:00). `heldSlots` is the total the tile prints; the two parts below
    // are the decomposition AC-1 Q2's Label requirement asks for, and they sum to it.
    let heldSlots = 0;
    let heldWithAgents = 0;
    let heldWithoutAgents = 0;
    let heldUnknown = 0;
    const reaching = new Set<string>();
    const reachingNone = new Set<string>();
    const reachUnknown = new Set<string>();

    for (const r of rows) {
      // Both counters answer ONE question — the label's: how much access-list space
      // is a silent address occupying. So both are scoped to the silent addresses
      // (`addressLive === false`, the D6 verdict: no agent behind this address is
      // alive), and a slot is held whenever the list was read and the address was in
      // it, whatever entry state that produced.
      //
      // Deliberately NOT `p.reclaimable`. That field is state ∈ {no_agent, stale} AND
      // silent — "what root may delete right now", a strictly narrower set than "what
      // is being wasted". A silent address sitting in `in_sync`, `manual` or
      // `open_to_all` still holds a slot, and counting `reclaimable` here understated
      // the waste on the one screen whose subject is a 200-slot budget (F-QC1: the
      // label's answer was 5, the tile said 2). `reclaimable` is untouched and stays
      // the only thing the selection control keys on (D6).
      //
      // The total is decomposed by `agentsTotal`, not by any verdict field: a slot on
      // an address that HAS agents (all of them silent) is a node that
      // died, and one on an address with `agentsTotal === 0` is an address nothing
      // reports. Different situations, different next move, so the operator must be
      // able to take the number apart. Still derived only from `addressLive` and
      // `agentsTotal` — both contract fields — and still never from `reclaimable` (D6),
      // even though branch 3 of `classifyEntries` makes the first part coincide with it.
      if (!r.addressLive) {
        for (const p of r.projects) {
          if (doKeys.has(p.projectKey)) continue;
          if (p.inLiveList === true) {
            heldSlots += 1;
            if (r.agentsTotal > 0) heldWithAgents += 1;
            else heldWithoutAgents += 1;
          } else if (p.inLiveList == null) heldUnknown += 1;
        }
      }

      // Only an address something actually reports can "reach no list" — a row that
      // exists purely because Atlas has an entry says nothing about a cluster.
      if (r.agentsTotal > 0) {
        const cluster = r.clusterName ?? '(no cluster recorded)';
        const anyLive = r.projects.some((p) => p.inLiveList === true);
        const anyUnknown = r.projects.some((p) => p.inLiveList == null);
        if (anyLive) reaching.add(cluster);
        // No project row at all is NOT "we haven't looked" — it is the finding
        // itself: an agent is reporting an address that no Atlas project wants and
        // no access list holds. `unknown` is reserved for the case where a project
        // DOES claim the address but its list has never been read (D10).
        else if (!r.projects.length) reachingNone.add(cluster);
        else if (anyUnknown) reachUnknown.add(cluster);
        else reachingNone.add(cluster);
      }
    }
    return {
      heldSlots,
      heldWithAgents,
      heldWithoutAgents,
      heldUnknown,
      reaching: reaching.size,
      reachingNone: reachingNone.size,
      reachUnknown: reachUnknown.size,
    };
  }, [rows, doKeys]);

  const n = (v: number) => (known ? v : '—');

  return (
    <div className="ca-tiles">
      <section className="ca-tile">
        <div className="ca-tile__n">{n(t.heldSlots)}</div>
        {/* The count includes addresses with NO agent at all (`agentsTotal === 0`:
            `no_agent`, `manual`, `open_to_all`) — 4 of the fixture's 5. "whose agents
            have all been silent" presupposes agents exist, so it was false for exactly
            those, and the widened count made it false for most of the number. It names
            AGENT_RED_MINUTES (AC-14) rather than restating the window as a second
            number. */}
        <div className="ca-tile__label">
          Access-list slots held by addresses with no agent heartbeat in the last{' '}
          {AGENT_RED_MINUTES} min
        </div>
        {/* AC-1 Q2 requires the number to stay decomposable on screen, because the two
            halves call for different actions: a slot on an address that HAS agents is a
            node that died, reclaimable from its project drawer below, while a slot on an
            address with no agent at all is something to look into first — it may be
            waste, or it may be the bastion the classifier files as `manual` whenever it
            cannot recognise a comment. Each half is written to be true on its own: the
            first presupposes agents and the second must not, which is the test the
            original label failed. The window is restated from the same constant, so the
            halves read standalone without ever becoming a second number (AC-14). */}
        {known && (
          <div className="ca-tile__sub muted">
            {`Made up of ${t.heldWithAgents} slot${t.heldWithAgents === 1 ? '' : 's'} on addresses that have agents, every one silent past ${AGENT_RED_MINUTES} min — and ${t.heldWithoutAgents} on addresses with no agent at all.`}
          </div>
        )}
        <div className="ca-tile__sub muted">
          {known
            ? `${t.heldUnknown} more slot${t.heldUnknown === 1 ? '' : 's'} unknown — that project's list has never been read`
            : 'not available yet'}
        </div>
      </section>

      <section className="ca-tile">
        <div className="ca-tile__n">{n(t.reachingNone)}</div>
        <div className="ca-tile__label">
          Clusters reporting an address that reaches no access list at all
        </div>
        <div className="ca-tile__sub muted">
          {known
            ? `${t.reaching} reaching a list · ${t.reachingNone} reaching none · ${t.reachUnknown} unknown`
            : 'not available yet'}
        </div>
      </section>

      {/* The third tile — "Egress addresses this screen knows about" — went with the
          table on 2026-08-26. It counted the table's own rows: with no table, it
          measured a thing that is no longer on the screen, and "this screen knows
          about" was the only honest wording left for a number nobody could open. The
          two tiles above are facts about the ESTATE, not about a component, so they
          survive the table they used to sit over. */}
    </div>
  );
}
